import { PencilIcon, PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import { randomUUID } from "crypto";
import { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Modal } from "src/components/DialogComponent";
import { SearchUI } from "src/components/ui/Search";
import TGButton from "src/components/ui/TGButton";
import { Option, TGSelectPickerByList } from "src/components/ui/TGSelect";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";

import { useAdditionalFieldsStore } from "../stores/additionalFieldsStore";
import { isEqual } from "lodash";

const additionalList: FieldType[] = [
  { id: "1", name: "Выбор локации", type: "location" },
  { id: "2", name: "Выбор отдела", type: "department" },
  { id: "3", name: "Выбор участка", type: "sector" },
  { id: "4", name: "Выбор сотрудника", type: "employee" },
  { id: "5", name: "Выбор даты", type: "date" },
  { id: "6", name: "Одиночный выбор из списка", type: "single" },
  { id: "7", name: "Свободный текст", type: "free" },
  { id: "8", name: "Множественный выбор", type: "many" },
  { id: "9", name: "Пятибалльная шкала", type: "grade" },
];

const typeMap = {
  location: "Выбор локации",
  department: "Выбор отдела",
  sector: "Выбор участка",
  employee: "Выбор сотрудника",
  date: "Выбор даты",
  single: "Одиночный выбор из списка:",
  free: "Свободный текст",
  many: "Множественный выбор:",
  grade: "Пятибалльная шкала",
};

export type FieldType = {
  id: string;
  name: string;
  type: string;
  value?: any;
  options?: Option[];
};

function AdditionalMeetingFields() {
  const { fields } = useAdditionalFieldsStore();

  const [open, setOpen] = useState(false); //! изменить на false

  function toggleOpen(value: boolean) {
    setOpen(value);
  }

  return (
    <div>
      <AdditionalMeetingFieldsList fields={fields} />
      {/* Кнопка открытия модалки доп полей */}
      <TGButton
        type="button"
        className="w-full mt-2.5"
        icon={<PlusCircleIcon width={20} height={20} />}
        onClick={() => toggleOpen(true)}
      >
        Добавить поле
      </TGButton>
      <AdditionalModal open={open} toggleOpen={toggleOpen} />
    </div>
  );
}

export default AdditionalMeetingFields;

const getDefaultTypeField = (defaultType: string) => additionalList.filter((field) => field.type === defaultType);
function isSelectFields(type: FieldType) {
  return type.id === "6" || type.id === "8";
}

function AdditionalModal({ open, toggleOpen }: { open: boolean; toggleOpen: (value: boolean) => void }) {
  const { fields, append } = useAdditionalFieldsStore();

  const [typeField, setTypeField] = useState<FieldType[]>([additionalList[0]]);
  const [nameField, setNameField] = useState("");
  const [options, setOptions] = useState<Option[]>([]);

  function changeTypeField(type: FieldType[]) {
    setTypeField(type);
  }

  function submitAdditionalField(name: string) {
    setNameField(name);
  }

  function resetFields() {
    setTypeField([additionalList[0]]);
    setNameField("");
  }

  function addField() {
    const isCreatedWithSameName = fields.some((f) => f.name === nameField && f.value.id === typeField[0].id);

    if (nameField && !isCreatedWithSameName) {
      const submitField = {
        name: nameField,
        type: typeField[0].type, // type это сам строковый тип
        value: typeField[0],
        options: isSelectFields(typeField[0]) ? options : [],
      };

      append(submitField);
      resetFields();

      toggleOpen(false);
    }
  }

  function cancel() {
    resetFields();
    toggleOpen(false);
  }

  useEffect(() => {
    if (isSelectFields(typeField[0])) {
      setOptions([{ name: "", id: crypto.randomUUID() }]);
    } else {
      setOptions([]);
    }
  }, [typeField[0].id]);

  return (
    <Modal open={open} toggleDialog={cancel} modalClassName="min-h-0 custom-selects">
      <FlexCol className="shrink">
        <h1 className="px-10 text-black">Добавить дополнительное поле</h1>
        <div className="w-full py-5 px-10 my-[18px]">
          <div>
            <TGSelectPickerByList<FieldType>
              values={typeField}
              onChange={changeTypeField}
              fetchKey="additionalField"
              classNames={{
                selectClassName: "!px-5",
                labelClassName: "text-base font-normal",
                dropdownClassName: "w-full",
              }}
              searchPlaceholder="Выберите тип поля"
              dataField={{
                placeholder: "Выберите тип поля",
                type: "peoplePicker",
                require: true,
                title: "Тип поля",
                width: "w-full",
                key: "additionalField",
                single: true,
                hideRemoveButton: true,
              }}
              list={additionalList}
              errors={{}}
            />
          </div>
          <div className="w-full">
            <SearchUI
              search={false}
              delay={0}
              require={true}
              label="Название поля"
              placeholder="Введите название поля"
              defaultValue={nameField}
              onClick={submitAdditionalField}
            />
            {(typeField[0].id === "6" || typeField[0].id === "8") && (
              <div className="mt-5 text-primaryGray">
                <h4 className="mb-2.5">Вариант ответа</h4>
                <div className="p-5 rounded-lg bg-gray2">
                  <OptionsInput options={options} setOptions={(options) => setOptions(options)} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between px-10 pt-10 border-t">
          <TGButton type="button" onClick={cancel}>
            Отмена
          </TGButton>
          <TGButton
            type="button"
            color="bluedark"
            disabled={
              !typeField.length ||
              !nameField.length ||
              (isSelectFields(typeField[0]) && (!options.length || options.some((f: Option) => f.name === "")))
            }
            onClick={addField}
          >
            Добавить поле
          </TGButton>
        </div>
      </FlexCol>
    </Modal>
  );
}

function OptionsInput({ options, setOptions }: { options: Option[]; setOptions: (options: Option[]) => void }) {
  function addOption() {
    setOptions([...options, { name: "", id: crypto.randomUUID() }]);
  }

  function changeOption(id: string, value: string) {
    const newOptions = options.map((o) => {
      return o.id === id ? { ...o, name: value } : o;
    });
    setOptions(newOptions);
  }

  function removeOption(id: string) {
    const newOptions = options.filter((o) => o.id !== id);
    setOptions(newOptions);
  }

  return (
    <>
      {options.length
        ? options.map((option) => (
            <OptionInput key={option.id} option={option} changeOption={changeOption} removeOption={removeOption} />
          ))
        : null}
      <TGButton onClick={addOption} className="text-[#637381] w-full" icon={<PlusCircleIcon width={20} height={20} />}>
        Добавить вариант ответа
      </TGButton>
    </>
  );
}

function OptionInput({
  option,
  changeOption,
  removeOption,
}: {
  option: Option;
  changeOption: (id: string, value: string) => void;
  removeOption: (value: string) => void;
}) {
  return (
    <div className="flex items-center pb-5 space-x-5 flex-nowrap">
      <SearchUI
        name={option.name}
        defaultValue={option.name}
        onClick={(e: string) => {
          changeOption(option.id, e);
        }}
        delay={0}
        placeholder="Введите вариант ответа"
        search={false}
      />
      <div className="bg-white w-[46px] h-[46px] rounded-lg shadow-custom items-center justify-center flex shrink-0 hover:bg-gray-100 hover">
        <TGButton
          onClick={() => removeOption(option.id)}
          iconOnly={true}
          icon={<TrashIcon width={20} height={20} />}
          color="white"
          className="w-full h-full"
        />
      </div>
    </div>
  );
}

function AdditionalMeetingFieldsList({ fields }: { fields: FieldType[] }) {
  return (
    <FlexCol className="w-full gap-y-2.5">
      {fields.map((item, index) => (
        <Item key={item.id} item={item} disabled={false} index={index} />
      ))}
    </FlexCol>
  );
}

function Item({ item, disabled, index }: { item: FieldType; disabled: boolean; index: number }) {
  const { remove, update } = useAdditionalFieldsStore();

  const [openEditModal, setOpenEditModal] = useState(false);

  function toggleOpenEditModal(value: boolean) {
    setOpenEditModal(value);
  }

  function editFieldHandle(value: FieldType) {
    // изменить поле

    update(index, value);
  }

  function removeFieldHandle(value: number) {
    // изменить поле

    remove(value);
  }

  return (
    <FlexRow className="p-5 bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg cursor-pointer items-center" key={item.id}>
      <FlexCol className="w-full">
        <FlexRow className="justify-between">
          <FlexCol className="justify-center shrink">
            <h3 className="font-medium text-black">{item.name}</h3>
          </FlexCol>

          <FlexCol className=" text-[#637381] w-[4%]">
            {disabled ? null : (
              <FlexRow className="space-x-2.5">
                <FlexCol>
                  <TGButton
                    type="button"
                    iconOnly={true}
                    icon={<PencilIcon width={24} height={24} />}
                    className="w-6 h-10"
                    onClick={() => toggleOpenEditModal(true)}
                  />
                </FlexCol>
                <FlexCol>
                  <TGButton
                    type="button"
                    iconOnly={true}
                    icon={<TrashIcon width={24} height={24} />}
                    className="w-6 h-10"
                    onClick={() => removeFieldHandle(index)}
                  />
                </FlexCol>
              </FlexRow>
            )}
          </FlexCol>
        </FlexRow>

        <FlexRow className="text-[14px] text-primaryGray">
          {typeMap[item.type as keyof typeof typeMap]}
          {item.options?.length ? ` ${item.options.map((option) => option.name).join(", ")}` : ""}
        </FlexRow>
      </FlexCol>
      <EditModal
        open={openEditModal}
        toggleOpen={toggleOpenEditModal}
        onEditField={editFieldHandle}
        defaultField={item}
        typeModal="edit"
      />
    </FlexRow>
  );
}

function EditModal({
  open,
  toggleOpen,
  onEditField,
  defaultField,
}: {
  open: boolean;
  toggleOpen: (value: boolean) => void;
  onEditField: (value: FieldType) => void;

  typeModal?: string;
  defaultField?: FieldType;
}) {
  const [typeField, setTypeField] = useState<FieldType[]>([additionalList[0]]); //! изменить на 0
  const [nameField, setNameField] = useState("");
  const [options, setOptions] = useState<Option[]>([]);

  function changeTypeField(types: FieldType[]) {
    setTypeField(types);

    if (isSelectFields(typeField[0])) {
      setOptions([{ name: "", id: crypto.randomUUID() }]);
    } else {
      setOptions([]);
    }
  }

  function submitAdditionalField(name: string) {
    setNameField(name);
  }

  function returnFields() {
    changeField(defaultField);
  }

  function isChangedFields(defaultField: FieldType | undefined) {
    const isChangedName = defaultField?.name !== nameField;
    const isChangedType = defaultField?.type !== typeField[0].type;
    const isChangedOptions = isEqual(defaultField?.options, options);

    return isChangedName || isChangedType || !isChangedOptions;
  }

  function editField() {
    if (isChangedFields(defaultField)) {
      const field: FieldType = {
        id: defaultField ? defaultField.id : crypto.randomUUID(),
        name: nameField,
        type: typeField[0].type,
        value: typeField[0],
        options: isSelectFields(typeField[0]) ? options : [],
      };

      onEditField(field);
    }
  }

  function changeField(field: FieldType | undefined) {
    if (field) {
      const newDefaultType = getDefaultTypeField(field.type);

      setTypeField(newDefaultType);

      setNameField(field.name);

      if (!!field.options?.length) {
        setOptions(field.options);
      }
    }
  }

  function cancel() {
    returnFields();
    toggleOpen(false);
  }

  useEffect(() => {
    changeField(defaultField);
  }, [defaultField]);

  return (
    <Modal open={open} toggleDialog={cancel} modalClassName="min-h-0 custom-selects">
      <FlexCol className="shrink">
        <h1 className="px-10 text-black">Изменить дополнительное поле</h1>
        <div className="w-full py-5 px-10 my-[18px]">
          <div>
            <TGSelectPickerByList
              values={typeField}
              onChange={changeTypeField}
              fetchKey="additionalField"
              classNames={{
                selectClassName: "!px-5",
                labelClassName: "text-base font-normal",
                dropdownClassName: "w-full",
              }}
              searchPlaceholder="Выберите тип поля"
              dataField={{
                placeholder: "Выберите тип поля",
                type: "peoplePicker",
                require: true,
                title: "Тип поля",
                width: "w-full",
                key: "additionalField",
                single: true,
                hideRemoveButton: true,
              }}
              list={additionalList}
              errors={{}}
            />
          </div>
          <div className="w-full">
            <SearchUI
              name="searchType"
              search={false}
              delay={0}
              require={true}
              label="Название поля"
              placeholder="Введите название поля"
              defaultValue={nameField}
              onClick={submitAdditionalField}
            />
            {isSelectFields(typeField[0]) && (
              <div className="mt-5 text-primaryGray">
                <h4 className="mb-2.5">Вариант ответа</h4>
                <div className="p-5 rounded-lg bg-gray2">
                  <OptionsInput options={options} setOptions={(options) => setOptions(options)} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between px-10 pt-10 border-t">
          <TGButton type="button" onClick={cancel}>
            Отмена
          </TGButton>
          <TGButton
            type="button"
            color="bluedark"
            disabled={
              !typeField.length ||
              !nameField.length ||
              (isSelectFields(typeField[0]) && options.some((f: Option) => f.name === "")) ||
              !isChangedFields(defaultField)
            }
            onClick={editField}
          >
            Изменить поле
          </TGButton>
        </div>
      </FlexCol>
    </Modal>
  );
}
